import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import "./SpecificBiologySection.css";
import PulmonologySaveButton from "../PulmonologySaveButton/PulmonologySaveButton";

const bloodGasFields = [
  ["ph", "0", ""], ["pao2", "0", "mmHg"], ["paco2", "0", "mmHg"],
  ["bicarbonate", "0", "mmol/L"], ["sao2", "0", "%"], ["fio2", "0", "%"],
];
export default function SpecificBiologySection({ onContinue, record, saveRecord, saving, canSave }) {
  const { t } = useTranslation("pulmonology");
  const [days, setDays] = useState([]);
  const [abgValues, setAbgValues] = useState(() => Object.fromEntries(bloodGasFields.map(([key, value]) => [key, value])));
  const [biologyCards, setBiologyCards] = useState({
    quantiferon: { valueKey: "common.notTested", detail: "" },
    proBnp: { value: "0", unit: "pg/mL", detail: "" },
    pao2: { value: "0", unit: "mmHg", detail: "" },
  });
  const [editingBiologyCard, setEditingBiologyCard] = useState(null);
  const persistedRecord = Array.isArray(record) ? record[0] : record;
  const respiratoryAcidosis = Number(abgValues.ph) > 0
    && Number(abgValues.ph) < 7.35
    && Number(abgValues.paco2) > 45;

  useEffect(() => {
    if (!persistedRecord) return;
    if (persistedRecord.sputumCollectionDays) setDays(persistedRecord.sputumCollectionDays);
    if (persistedRecord.bloodGasValues) setAbgValues(persistedRecord.bloodGasValues);
    setBiologyCards((current) => {
      const nextCards = { ...current };
      if (persistedRecord.quantiferonResult) {
        nextCards.quantiferon = {
          value: persistedRecord.quantiferonResult,
          detail: persistedRecord.quantiferonDate ? String(persistedRecord.quantiferonDate).slice(0, 10) : "",
        };
      }
      if (persistedRecord.proBnpValue != null) nextCards.proBnp = { ...nextCards.proBnp, value: String(persistedRecord.proBnpValue), unit: persistedRecord.proBnpUnit || "" };
      if (persistedRecord.pao2Value != null) nextCards.pao2 = { ...nextCards.pao2, value: String(persistedRecord.pao2Value), unit: persistedRecord.pao2Unit || "" };
      return nextCards;
    });
  }, [persistedRecord]);

  const saveBiology = () => saveRecord?.("specific-biology", {
    sputumCollectionDays: days,
    bloodGasValues: abgValues,
    quantiferonResult: biologyCards.quantiferon.value || t(biologyCards.quantiferon.valueKey),
    quantiferonDate: biologyCards.quantiferon.detail || undefined,
    proBnpValue: Number.isFinite(Number(biologyCards.proBnp.value)) ? Number(biologyCards.proBnp.value) : undefined,
    proBnpUnit: biologyCards.proBnp.unit,
    pao2Value: Number.isFinite(Number(biologyCards.pao2.value)) ? Number(biologyCards.pao2.value) : undefined,
    pao2Unit: biologyCards.pao2.unit,
  }, persistedRecord?.id);
  const toggleDay = (day) => setDays((items) => items.includes(day) ? items.filter((item) => item !== day) : [...items, day]);

  return (
    <div className="pulmo-form">
      <div className="pulmo-subhead"><strong>{t("biology.sputumTitle")}</strong><span className="pulmo-tag is-info">{t("biology.daysCollected", { count: days.length })}</span></div>
      <div className="pulmo-days">{[1, 2].map((day) => <button type="button" className={`pulmo-day${days.includes(day) ? " is-done" : ""}`} key={day} onClick={() => toggleDay(day)}><span>{days.includes(day) ? <Check size={11} /> : day}</span><strong>{t("biology.day", { count: day })}<small>{day === 1 ? t("biology.dates.day1") : t("biology.dates.day2")}</small></strong><b>{days.includes(day) ? t("common.negative") : t("common.pending")}</b></button>)}</div>
      <div className="pulmo-biology-cards">{Object.entries(biologyCards).map(([key, card]) => {
        const title = key === "pao2" ? "PaO₂" : t(`biology.${key}`);
        return <div className={key === "pao2" ? "is-critical" : ""} key={key}>
          {editingBiologyCard === key ? <div className="pulmo-biology-card-edit" onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget)) setEditingBiologyCard(null);
          }}>
            <strong>{title}</strong>
            <input autoFocus aria-label={t("biology.editValue", { name: title })} value={card.value ?? t(card.valueKey)} onChange={(event) => setBiologyCards((items) => ({ ...items, [key]: { ...items[key], value: event.target.value, valueKey: null } }))} />
            <input aria-label={t("biology.editDetail", { name: title })} value={card.detail ?? t(card.detailKey)} onChange={(event) => setBiologyCards((items) => ({ ...items, [key]: { ...items[key], detail: event.target.value, detailKey: null } }))} onKeyDown={(event) => event.key === "Enter" && setEditingBiologyCard(null)} />
          </div> : <button type="button" className="pulmo-biology-card-display" aria-label={t("biology.editValue", { name: title })} onClick={() => setEditingBiologyCard(key)}>
            <small>{title}</small><strong>{card.value ?? t(card.valueKey)}{card.unit ? ` ${card.unit}` : ""}</strong><span>{card.detail ?? t(card.detailKey)}</span>
          </button>}
        </div>;
      })}</div>
      <div className="pulmo-abg"><div className="pulmo-subhead"><strong>{t("biology.bloodGases")}</strong><span className={`pulmo-tag${respiratoryAcidosis ? " is-danger" : " is-info"}`}>{t(respiratoryAcidosis ? "biology.acidosis" : "common.notTested")}</span></div><div className="pulmo-abg-grid">{bloodGasFields.map(([labelKey, , unit], index) => <label className={`pulmo-input-field${(index === 1 || index === 2) && Number(abgValues[labelKey]) > 0 ? " is-warning" : ""}`} key={labelKey}><span>{t(`biology.fields.${labelKey}`)}</span><span className="pulmo-input-wrap"><input value={abgValues[labelKey]} aria-label={t(`biology.fields.${labelKey}`)} onChange={(event) => setAbgValues((current) => ({ ...current, [labelKey]: event.target.value }))} onKeyDown={(event) => {
        if (event.key === "Enter") event.preventDefault();
      }} /><small>{unit}</small></span></label>)}</div></div>
      <div className="pulmo-gradient-note">{t("biology.gradient")} <strong>22 mmHg</strong><span>{t("biology.elevatedForAge")}</span></div>
      <div className="pulmo-actions">
        <PulmonologySaveButton onClick={saveBiology} saving={saving} disabled={!canSave} />
        <button type="button" className="pulmo-primary-button" onClick={() => onContinue?.("bilan")}>{t("biology.generateRequest")}</button>
      </div>
    </div>
  );
}
